<template>
  <ChatModal
    name="profile"
    width="550"
    :title="$t('profilePopupTitle')"
    @before-open="beforeOpen"
    @before-close="beforeClose"
  >
    <div v-if="user" slot="default">
      <div :class="$style.header">
        <div :class="$style.headerFlex">
          <Avatar :user="user" :size="51" :class="$style.avatar" v-viewer />
          <div :class="$style.info">
            <div :class="$style.name">{{ user.displayName }}</div>
            <div :class="[$style.meta, { [$style.offline]: !user.isOnline }]">{{ meta }}</div>
          </div>
        </div>
        <div v-if="status && isShowStatus" :class="$style.status">
          <BaseIcon name="info-circle" /> {{ status.statusText }}
        </div>
      </div>
      <div :class="$style.headerActions">
        <Fab v-if="!isMyUser && !isContact" @click.native="addToContacts" :class="$style.headerActionBtn">
          <BaseIcon name="user-plus" />
        </Fab>
        <Fab @click.native="goChat" :class="$style.headerActionBtn">
          <BaseIcon name="comment" />
        </Fab>
      </div>
      <tabs
        :tabs="tabs"
        :currentTab="currentTab"
        :class="$style.tabs"
        ref="tabs"
        @onClick="tab => (currentTab = tab)"
      />
      <div :class="$style.content">
        <div v-if="currentTab === 'info'" :class="$style.section">
          <div :class="$style.contactItem" v-show="!isUserAnonymous">
            <div :class="$style.contactItemLabel">{{ $t('formPhone') }}</div>
            <div :class="$style.contactItemText">{{ user.phone }}</div>
          </div>
          <div v-if="user.email" :class="$style.contactItem">
            <div :class="$style.contactItemLabel">{{ $t('formEmail') }}</div>
            <div :class="$style.contactItemText">{{ user.email }}</div>
          </div>
          <div v-if="user.nickname" :class="$style.sectionInfo">
            <div>
              <div :class="$style.contactInfoLabel">{{ $t('formNickName') }}</div>
              <div :class="$style.contactInfoText">@{{ user.nickname }}</div>
            </div>
            <img
              v-if="showQRBtn"
              src="../../../assets/img/qrcode-green.png"
              alt="QRCode"
              :class="$style.qrImg"
              @click="openQR"
            />
          </div>
          <template v-if="isShowSettingToggles">
            <BaseSwitch
              :value="chatSettings.enableNotifications"
              @input="val => changeSetting(val, 'enableNotifications')"
              :class="$style.contactItem"
              >{{ $t('chatSettings.enableNotifications') }}</BaseSwitch
            >
            <BaseSwitch
              :value="chatSettings.enableOnline"
              @input="val => changeSetting(val, 'enableOnline')"
              :class="$style.contactItem"
              >{{ $t('chatSettings.enableOnline') }}</BaseSwitch
            >
            <BaseSwitch
              :value="chatSettings.enableRead"
              @input="val => changeSetting(val, 'enableRead')"
              :class="$style.contactItem"
              >{{ $t('chatSettings.enableRead') }}</BaseSwitch
            >
            <BaseSwitch
              :value="chatSettings.enableTyping"
              @input="val => changeSetting(val, 'enableTyping')"
              :class="$style.contactItem"
              >{{ $t('chatSettings.enableTyping') }}</BaseSwitch
            >
          </template>
          <div>
            <div :class="$style.contactItemLabel">{{ $t('profileSharedMedia') }}</div>
            <div v-for="obj in mediaInfoList" :key="obj.key">
              <span v-if="isShowButton(obj.key)" class="link" @click="currentTab = obj.key">
                {{ obj.text }}
              </span>
              <template v-else>{{ obj.text }}</template>
            </div>
          </div>
          <div v-if="!isMyUser && isContact" :class="$style.bottomBlock">
            <span @click="removeFromContacts" :class="$style.remove">{{ $t('contact.delete') }}</span>
          </div>
        </div>
        <template v-if="oneToOneChat">
          <component :is="currentTabComponent" :chat="oneToOneChat" />
        </template>
      </div>
    </div>
  </ChatModal>
</template>

<script>
import ChatModal from '../ChatModal'
import Avatar from '@/components/Avatar'
import Fab from '@/components/Fab'
import { mapGetters, mapActions } from 'vuex'
import Tabs from 'vue-tabs-with-active-line'
import SharedPhoto from './SharedPhoto'
import SharedFiles from './SharedFiles'
import SharedGroups from './SharedGroups'
import SharedVoice from './SharedVoice'
import SharedAudio from './SharedAudio'
import SharedLinks from './SharedLinks'
import SharedSubjects from './SharedSubjects'
import { TYPE_CODE } from '@/models/Chat'

const MEDIA_KEYS_ORDER = [
  'attachmentsImg',
  'attachmentsVideo',
  'attachmentsFile',
  'attachmentsAudio',
  'links',
  'audios',
  'videos',
  'chatsCount',
  'subjects'
]
const componentByKey = {
  attachmentsImg: SharedPhoto,
  attachmentsFile: SharedFiles,
  attachmentsAudio: SharedAudio,
  links: SharedLinks,
  audios: SharedVoice,
  chatsCount: SharedGroups,
  subjects: SharedSubjects
}

export default {
  name: 'profile-popup',
  components: {
    ChatModal,
    Avatar,
    Fab,
    Tabs
  },
  data () {
    return {
      userId: null,
      chatId: null,
      beforeClose: () => {},
      currentTab: 'info'
    }
  },
  computed: {
    ...mapGetters({
      selfUser: 'auth/user',
      getUser: 'entities/users/find',
      contactIds: 'entities/users/contactIds',
      getChatWithUser: 'entities/chats/getChatWithUser',
      chatById: 'entities/chats/getById',
      findChatSettings: 'entities/chatSettings/find',
      status: 'entities/userStatuses/myCurrentStatus'
    }),
    tabs () {
      const tabs = [
        {
          title: this.$t('profileTab_info'),
          value: 'info'
        },
        {
          title: this.$t('profileTab_links'),
          value: 'links'
        },
        {
          title: this.$t('profileTab_photos'),
          value: 'attachmentsImg'
        },
        {
          title: this.$t('profileTab_files'),
          value: 'attachmentsFile'
        },
        {
          title: this.$t('profileTab_audio'),
          value: 'attachmentsAudio'
        },
        {
          title: this.$t('profileTab_voice'),
          value: 'audios'
        },
        {
          title: this.$t('profileTab_groups'),
          value: 'chatsCount'
        },
        {
          title: this.$t('profileTab_subjects'),
          value: 'subjects'
        }
      ]
      // return tabs.filter(tab => tab.value === 'info' || this.user.mediaInfo[tab.value])
      return tabs.map(tab => {
        if (tab.value !== 'info' && !this.user.mediaInfo[tab.value]) {
          return {
            ...tab,
            disabled: true
          }
        }
        return tab
      })
    },
    user () {
      return this.getUser(this.userId)
    },
    isUserAnonymous () {
      return this.user.phone.startsWith('+202')
    },
    isMyUser () {
      return this.selfUser.id === this.userId
    },
    isContact () {
      return this.contactIds.indexOf(this.userId) > -1
    },
    oneToOneChat () {
      return this.getChatWithUser(this.userId)
    },
    meta () {
      return this.user.isOnline ? this.$t('chat.online') : this.$t('chat.offline')
    },
    mediaInfoList () {
      return MEDIA_KEYS_ORDER.map(key => {
        if (!this.user.mediaInfo[key]) return
        return {
          key,
          text: this.$t(`profileMedia_${key}`, { n: this.user.mediaInfo[key] })
        }
      }).filter(o => o != null)
    },
    currentTabComponent () {
      return componentByKey[this.currentTab]
    },
    chat () {
      return this.chatById(this.chatId)
    },
    isShowSettingToggles () {
      return this.oneToOneChat && this.chatId && this.oneToOneChat.id === this.chatId
    },
    chatSettings () {
      return this.findChatSettings(this.chatId)
    },
    // status () {
    //   if (!this.user.statusId) return
    //   return this.getStatus(this.user.statusId)
    // },
    isShowStatus () {
      return this.user.statusExpiresTs && this.user.statusExpiresTs > Date.now()
    },
    showQRBtn () {
      return this.contactIds.includes(this.user?.userId)
    }
  },
  watch: {
    currentTab (currentTab) {
      const tabs = this.$refs.tabs
      const tabEl = this.$refs.tabs.$refs[currentTab]
      if (!tabs || !tabs.$el || !tabEl || !tabEl[0]) return
      this.$scrollTo(tabEl[0], 600, {
        container: tabs.$el,
        x: true,
        y: false,
        easing: 'ease-in'
      })
    }
  },
  methods: {
    beforeOpen ({ params: { userId, chatId, onClose = () => {} } }) {
      this.beforeClose = onClose
      this.currentTab = 'info'
      this.userId = userId
      this.chatId = chatId
      this.fetchUsers([userId])
      if (this.oneToOneChat) {
        this.getMedia({ userId, chatId: this.oneToOneChat.id })
      }
    },
    ...mapActions({
      fetchUsers: 'entities/users/getProfiles',
      createChat: 'entities/chats/create',
      addContact: 'entities/users/addContact',
      removeContact: 'entities/users/removeContact',
      getMedia: 'entities/chats/getMedia',
      updateSettings: 'entities/chatSettings/updateByChat'
    }),
    async goChat () {
      const { id } = await this.createChat({
        type: TYPE_CODE['one-to-one'],
        participants: [this.userId]
      })
      this.$modal.hide('profile')
      this.$modal.hide('group-profile')
      this.$router.push({ name: 'app-chat', query: { id: window.btoa(id) } })
    },
    addToContacts () {
      this.$modal.show('confirm', {
        content: this.$t('contact.addConfirmation', { name: this.user.displayName }),
        onSubmit: () => {
          this.addContact(this.user)
        }
      })
    },
    removeFromContacts () {
      this.$modal.show('confirm', {
        content: this.$t('contact.removeConfirmation', { name: this.user.displayName }),
        onSubmit: () => {
          this.removeContact(this.user)
        }
      })
    },
    isShowButton (key) {
      return this.tabs.find(tab => tab.value === key) && this.user.mediaInfo[key]
    },
    changeSetting (value, key) {
      if (!this.chatId || !this.chatSettings) return
      this.updateSettings({
        chatId: this.chatId,
        [key]: value
      })
    },
    openQR () {
      this.$modal.show('share-contact-by-code', {
        nickname: this.user.nickname,
        userId: this.user.userId
      })
    }
  }
}
</script>

<style lang="scss" module>
.header {
  background-color: $main-bg-colored;
  // height: 90px;
  padding: 10px 20px 20px;
}
.headerFlex {
  display: flex;
  align-items: center;
}
.avatar {
  margin-right: 12px;
  :global(img) {
    cursor: pointer;
  }
}
.info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: $text-lightest;
}
.name {
  font-size: 16px;
  font-weight: 500;
}
.meta {
  font-weight: 300;
}
.offline {
  color: $text-light;
}
.status {
  margin-top: 12px;
  color: $text-lightest;
  font-weight: 300;
}

.content {
  padding: 20px;
}

.section {
  // padding: 20px 10px 20px 62px;
  padding: 0 10px 0 62px;
}
.contactItem {
  margin-bottom: 18px;
  &Label {
    color: $text-mid;
    margin-bottom: 4px;
  }
  &Text {
    font-size: 16px;
  }
}

.headerActions {
  position: relative;
  top: -20px;
  display: flex;
  justify-content: flex-end;
  padding-right: 60px;
}
.headerActionBtn {
  margin-left: 24px;
}
.sectionInfo {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 18px;
  &Label {
    color: $text-mid;
    margin-bottom: 4px;
  }
  &Text {
    font-size: 16px;
  }
}
.qrImg {
  height: 32px;
  width: 32px;
  cursor: pointer;
}
.bottomBlock {
  margin-top: 40px;
}
.remove {
  color: $text-error;
  font-size: 14px;
  cursor: pointer;
}
.tabs {
  width: 100%;
  overflow-x: scroll;
  // justify-content: space-around;
  :global(.tabs__item) {
    flex-basis: auto;
    flex-grow: 0;
  }
}
</style>
